import { withZod } from '@remix-validated-form/with-zod'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewWorkspaceIntegrationModal } from '../AddNewWorkspaceIntegrationModal'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormInput from 'app/components/form/FormInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import { Tooltip } from 'app/components/Tooltip'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { useLocationStore } from 'app/stores/StoreContext'

const createSchema = z.object({
  apiKey: z.string().min(1, { message: `API Key is required` }),
  apiType: z.string().optional(),
})

export const createJotformWorkspaceIntegrationValidator = withZod(createSchema)

export function JotformWorkspaceForm({
  onIntegrationChanged,
  currentValues,
  orgId,
}: {
  onIntegrationChanged: () => void
  currentValues?: {
    apiKey?: string
    hipaa?: boolean
  }
  orgId?: string
}) {
  const [confirm] = useConfirmation()
  const [isRemovable, setIsRemovable] = useState(false)
  const locationStore = useLocationStore()

  useEffect(() => {
    if (currentValues) {
      bg(async () => {
        const result = await locationStore.fetchList()
        const data = result.data as any
        const hits = data?.locations?.hits ?? data?.hits ?? locationStore.hits
        if (hits.some((location) => location.jotformFormIds?.length)) {
          setIsRemovable(false)
        } else {
          setIsRemovable(true)
        }
      })
    }
  }, [currentValues])

  const onCreateCompleted = (data: any) => {
    if (data.success) {
      notifySuccess(`JotForm workspace integration saved`)
      onIntegrationChanged()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data.error}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewWorkspaceIntegrationModal onIntegrationChanged={onIntegrationChanged} />,
      {
        title: `Add New Workspace Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove JotForm Workspace Integration?`,
      description: `Removing this workspace integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() => {
      return window
        .fetch(`/api/integrations/setup/jotform`, {
          method: `DELETE`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({}),
        })
        .then((r) => r.json())
        .then((res: any) => {
          if (!res.success) {
            notifyError(`Failed to remove integration: ${res.message}`)
          } else {
            notifySuccess(`JotForm workspace integration removed`)
            onIntegrationChanged()
            closeModal()
          }
        })
        .catch((error) => {
          notifyError(`Failed to remove integration: ${error}`)
        })
    })
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 p-4"
        validator={createJotformWorkspaceIntegrationValidator}
        reloadDocument={false}
        shouldPreventDefault={true}
        onPreventDefault={(data) => onCreateCompleted(data)}
        action="/api/integrations/setup/jotform"
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        defaultValues={{
          apiKey: currentValues?.apiKey,
          apiType: currentValues?.hipaa !== false ? `hipaa` : `standard`,
        }}
      >
        <FormInput
          name="apiKey"
          cols={6}
          required
          label="API Key"
          labelClassName="text-body-sm text-strong"
          newLineError={false}
          showErrorIcon
        />
        {!isSMCOrg(orgId) && (
          <FormSelect
            name="apiType"
            cols={6}
            label="API Type"
            defaultValue={currentValues?.hipaa !== false ? `hipaa` : `standard`}
            options={[
              { label: `HIPAA`, value: `hipaa` },
              { label: `Standard`, value: `standard` },
            ]}
          />
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
        <div className="flex justify-between align-middle">
          <div>
            {currentValues ? (
              <Tooltip
                message={
                  !isRemovable
                    ? `You need to first delete all JotForm location integrations to be able to remove this workspace integration.`
                    : undefined
                }
              >
                <Button label="Remove" mode="danger" size="lg" disabled={!isRemovable} onClick={() => onRemove()} />
              </Tooltip>
            ) : null}
          </div>
          <div className="flex justify-end gap-x-4 align-middle">
            {!currentValues ? (
              <Button label="Back" mode="white" size="lg" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="lg" onClick={() => closeModal()} />
            )}
            <Button type="submit" form="integration-form" label="Save" mode="primary" size="lg" />
          </div>
        </div>
      </div>
    </>
  )
}
